import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Upload, BarChart3, MessageCircle, Download, ChevronRight, X, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const STEPS = [
  {
    icon: Upload,
    title: 'Upload Your Data',
    description: 'Drag & drop a CSV or XLSX file, or paste a Google Sheets URL to get started.',
    color: 'text-primary bg-primary/10',
  },
  {
    icon: BarChart3,
    title: 'Instant Analysis',
    description: 'Get auto-generated charts, statistics, and data quality insights for every sheet.',
    color: 'text-emerald-600 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-950/30',
  },
  {
    icon: MessageCircle,
    title: 'Ask AI Questions',
    description: 'Chat with your data using natural language. Get answers about trends, patterns, and anomalies.',
    color: 'text-amber-600 bg-amber-50 dark:text-amber-400 dark:bg-amber-950/30',
  },
  {
    icon: Download,
    title: 'Export & Share',
    description: 'Download your analysis as PDF, Markdown, or CSV to share with your team.',
    color: 'text-violet-600 bg-violet-50 dark:text-violet-400 dark:bg-violet-950/30',
  },
];

const OnboardingTour = () => {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    const completed = localStorage.getItem('onboarding_completed');
    if (!completed) {
      setVisible(true);
    }
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem('onboarding_completed', 'true');
  };

  const next = () => {
    if (step < STEPS.length - 1) setStep(step + 1);
    else dismiss();
  };

  if (!visible) return null;

  const { icon: Icon, title, description, color } = STEPS[step];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: 'spring', duration: 0.4 }}
        >
          <Card className="w-full max-w-md shadow-2xl">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <span className="text-xs font-medium text-muted-foreground">
                    Getting Started — {step + 1} / {STEPS.length}
                  </span>
                </div>
                <button onClick={dismiss} className="text-muted-foreground hover:text-foreground transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                  className="text-center py-4"
                >
                  <div className={`w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center ${color}`}>
                    <Icon className="w-7 h-7" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
                </motion.div>
              </AnimatePresence>

              {/* Progress dots */}
              <div className="flex items-center justify-center gap-1.5 mt-4 mb-5">
                {STEPS.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setStep(i)}
                    className={`w-2 h-2 rounded-full transition-colors ${i === step ? 'bg-primary w-5' : 'bg-border hover:bg-muted-foreground/30'}`}
                  />
                ))}
              </div>

              <div className="flex items-center justify-between">
                <Button variant="ghost" size="sm" onClick={dismiss} className="text-muted-foreground">
                  Skip tour
                </Button>
                <Button size="sm" onClick={next} className="gap-1">
                  {step === STEPS.length - 1 ? "Let's go!" : 'Next'}
                  {step < STEPS.length - 1 && <ChevronRight className="w-3.5 h-3.5" />}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default OnboardingTour;
